
import Vue from "vue";
import store from "@/store";
import api from "@/api/signup/api";
import isEqual from "lodash/isEqual";
import { APPLICATION } from "@/store/applications";
import { createNamespacedHelpers } from "vuex";
import { IApplicationState } from "@/store/applications/types";
import { parseStreetAddress } from "@/helpers/address";
import { ReferenceDataType } from "@/store/referenceData/types";
import { getReferenceDataDescription } from "@/helpers/referenceData";
import { requiredIf } from "vuelidate/lib/validators";
import { onlyDigits } from "@/validators";
import {
  CANCELLABLE_STATUSES,
  EDITABLE_STATUSES
} from "@/constants/applicationStatuses";

const { mapGetters, mapState } = createNamespacedHelpers(
  APPLICATION.NAME_SPACE
);

export default Vue.extend({
  watch: {
    "$v.$invalid": "setValidity"
  },
  computed: {
    physicalAddressDetails()         { return store.state.application.physicalAddressLookup; },
    physicalAddressLookupIsLoading() { return store.state.application.physicalAddressLookupIsLoading; },
    deliveryAddressDetails()         { return store.state.application.deliveryAddressLookup; },
    deliveryAddressLookupIsLoading() { return store.state.application.deliveryAddressLookupIsLoading; },
    overridePhysicalAddress()        { return store.state.application.ifcsOverrideData &&
                                              store.state.application.ifcsOverrideData.physicalAddress; },
    overridePostalAddress()          { return store.state.application.ifcsOverrideData &&
                                              store.state.application.ifcsOverrideData.postalAddress; },

    applicationType() {
      const promoApplicationType =
        store.state.promoOffer.promoCode &&
        store.state.promoOffer.promoCode.applicationType;

      const standardApplicationType =
        store.state.standardOffer.offer &&
        store.state.standardOffer.offer.applicationType;

      const applicationType = promoApplicationType || standardApplicationType;

      return getReferenceDataDescription(
        applicationType,
        ReferenceDataType.ApplicationType
      );
    },

    physicalStreet: {
      get(): any {
        if (this.overridePhysicalAddress) return this.overridePhysicalAddress.line1;
        return (parseStreetAddress(this.physicalAddressDetails) || (this as any).physicalAddressText);
      },
      set(value: string) {
        this.savePhysicalAddress(value, this.physicalTown, this.physicalPostCode, this.physicalCountry);
      }
    },
    physicalTown: {
      get(): any {
        if (this.overridePhysicalAddress) return this.overridePhysicalAddress.line2;
        return (this.physicalAddressDetails && this.physicalAddressDetails.cityTown) ||
               (this.physicalAddressDetails && this.physicalAddressDetails.mailTown);
      },
      set(value: string) {
        this.savePhysicalAddress(this.physicalStreet, value, this.physicalPostCode, this.physicalCountry);
      }
    },
    physicalPostCode: {
      get(): any {
        if (this.overridePhysicalAddress) return this.overridePhysicalAddress.postCode;
        return (this.physicalAddressDetails && this.physicalAddressDetails.postcode);
      },
      set(value: string) {
        this.savePhysicalAddress(this.physicalStreet, this.physicalTown, value, this.physicalCountry);
      }
    },
    physicalCountry: {
      get(): any {
        if (this.overridePhysicalAddress) return this.overridePhysicalAddress.country;
        return "New Zealand";
      },
      set(value: string) {
        this.savePhysicalAddress(this.physicalStreet, this.physicalTown, this.physicalPostCode, value);
      }
    },

    postalStreet: {
      get(): any {
        if (this.overridePostalAddress) return this.overridePostalAddress.line1;
        return (parseStreetAddress(this.deliveryAddressDetails, true) || (this as any).deliveryAddressText);
      },
      set(value: string) {
        this.savePostalAddress(value, this.postalTown, this.postalPostCode, this.postalCountry);
      }
    },
    postalTown: {
      get(): any {
        if (this.overridePostalAddress) return this.overridePostalAddress.line2;
        return (this.deliveryAddressDetails && this.deliveryAddressDetails.cityTown) ||
               (this.deliveryAddressDetails && this.deliveryAddressDetails.mailTown);
      },
      set(value: string) {
        this.savePostalAddress(this.postalStreet, value, this.postalPostCode, this.postalCountry);
      }
    },
    postalPostCode: {
      get(): any {
        if (this.overridePostalAddress) return this.overridePostalAddress.postCode;
        return (this.deliveryAddressDetails && this.deliveryAddressDetails.postcode);
      },
      set(value: string) {
        this.savePostalAddress(this.postalStreet, this.postalTown, value, this.postalCountry);
      }
    },
    postalCountry: {
      get(): any {
        if (this.overridePostalAddress) return this.overridePostalAddress.country;
        return "New Zealand";
      },
      set(value: string) {
        this.savePostalAddress(this.postalStreet, this.postalTown, this.postalPostCode, value);
      }
    },

    canSave(): boolean {
      return this.hasChanges && !this.isLoading && this.isValid;
    },
    isLoading(): boolean {
      return this.$store.state.ui.isLoading || this.$store.state.application.isLoading;
    },
    hasChanges(): boolean {
      const changes = store.state.application.ifcsOverrideData;
      const original = store.state.application.ifcsOverrideDataOriginal;

      return !isEqual(original, changes);
    },
    isValid(): boolean {
      return this.validity;
    },
    isEditable(): boolean {
      return (this.isLoading || EDITABLE_STATUSES.includes((this as any).applicationStatus));
    },
    canCancel(): boolean {
      return (this.isLoading || CANCELLABLE_STATUSES.includes((this as any).applicationStatus));
    },
    ...mapState({
      applicantFullName: (state: IApplicationState) =>
        state.applicationData && state.applicationData.fullName,
      applicantEmailAddress: (state: IApplicationState) =>
        state.applicationData && state.applicationData.email,
      contactNumber: (state: IApplicationState) =>
        state.applicationData &&
        state.applicationData.finalDetails &&
        state.applicationData.finalDetails.contactNumber,
      businessName: (state: IApplicationState) =>
        state.applicationData &&
        state.applicationData.businessDetails &&
        state.applicationData.businessDetails.businessName,
      tradingName: (state: IApplicationState) =>
        (state.applicationData &&
          state.applicationData.businessDetails &&
          state.applicationData.businessDetails.tradingName) ||
        (state.applicationData &&
          state.applicationData.businessDetails &&
          state.applicationData.businessDetails.businessName),
      NZBNID: (state: IApplicationState) =>
        state.applicationData &&
        state.applicationData.businessDetails &&
        state.applicationData.businessDetails.business &&
        state.applicationData.businessDetails.business.nzbn,
      physicalAddress: () =>
        store.state.application.applicationData &&
        store.state.application.applicationData.businessDetails &&
        store.state.application.applicationData.businessDetails.physicalAddress,
      physicalAddressText: () =>
        store.state.application.applicationData &&
        store.state.application.applicationData.businessDetails &&
        store.state.application.applicationData.businessDetails
          .physicalAddressText,
      deliveryAddress: () =>
        store.state.application.applicationData &&
        store.state.application.applicationData.finalDetails &&
        store.state.application.applicationData.finalDetails.deliveryAddress,
      deliveryAddressText: () =>
        store.state.application.applicationData &&
        store.state.application.applicationData.finalDetails &&
        store.state.application.applicationData.finalDetails.deliveryAddressText,
      applicationStatus: (state: IApplicationState) =>
        state.applicationData && state.applicationData.status
    })
  },
  data() {
    return {
      validity: false as boolean
    };
  },
  methods: {
    savePhysicalAddress(street: string, town: string, postCode: string, country: string) {
      this.$store.commit(APPLICATION.NS(APPLICATION.MUTATIONS.SET_IFCS_PHYSICAL_STREET), street);
      this.$store.commit(APPLICATION.NS(APPLICATION.MUTATIONS.SET_IFCS_PHYSICAL_TOWN), town);
      this.$store.commit(APPLICATION.NS(APPLICATION.MUTATIONS.SET_IFCS_PHYSICAL_POSTCODE), postCode);
      this.$store.commit(APPLICATION.NS(APPLICATION.MUTATIONS.SET_IFCS_PHYSICAL_COUNTRY), country);
    },
    savePostalAddress(street: string, town: string, postCode: string, country: string) {
      this.$store.commit(APPLICATION.NS(APPLICATION.MUTATIONS.SET_IFCS_POSTAL_STREET), street);
      this.$store.commit(APPLICATION.NS(APPLICATION.MUTATIONS.SET_IFCS_POSTAL_TOWN), town);
      this.$store.commit(APPLICATION.NS(APPLICATION.MUTATIONS.SET_IFCS_POSTAL_POSTCODE), postCode);
      this.$store.commit(APPLICATION.NS(APPLICATION.MUTATIONS.SET_IFCS_POSTAL_COUNTRY), country);
    },
    setValidity(key: string, valid: boolean) {
      this.validity = valid;
    },
    onSaveChanges() {
      const applicationData = store.state.application.applicationData;
      const ifcsData        = store.state.application.ifcsOverrideData;

      this.$store.dispatch(
        APPLICATION.NS(APPLICATION.ACTIONS.UPDATE_IFCS_DETAILS),
        { id: applicationData && applicationData.id, ifcsData }
      );
    },
    onCancelChanges() {
      this.$store.commit(APPLICATION.NS(APPLICATION.MUTATIONS.SET_IFCS_DATA),
                         store.state.application.ifcsOverrideDataOriginal);
    }
  },
  validations: {
    physicalStreet:   { required: requiredIf(vm => vm.isEditable) },
    physicalTown:     { required: requiredIf(vm => vm.isEditable) },
    physicalPostCode: { required: requiredIf(vm => vm.isEditable),
                        async isValid(value: any) { return value && value.length === 4 && onlyDigits(value); }},
    physicalCountry:  { required: requiredIf(vm => vm.isEditable) },
    postalStreet:     { required: requiredIf(vm => vm.isEditable) },
    postalTown:       { required: requiredIf(vm => vm.isEditable) },
    postalPostCode:   { required: requiredIf(vm => vm.isEditable),
                        async isValid(value: any) { return value && value.length === 4 && onlyDigits(value); }},
    postalCountry:    { required: requiredIf(vm => vm.isEditable) }
  }
});
